import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { SvgAngehaengteDokumente } from '../../../helpers/IconLoader';
import styles from './styles.jss';

const DEFAULT_EXTS = ['.heic', '.heif', '.jpg', '.jpeg', '.pdf', '.tif', '.tiff', '.png', '.bmp'];
const DEFAULT_MIME = [
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/bmp',
  'application/pdf',
];

const MAX_BYTES = 5 * 1024 * 1024;

const parseAccept = (accept) => {
  if (!accept) return { exts: DEFAULT_EXTS, mimes: DEFAULT_MIME };
  const parts = accept.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  const exts = parts.filter((p) => p.startsWith('.'));
  const mimes = parts.filter((p) => p.includes('/'));
  return {
    exts: exts.length ? exts : DEFAULT_EXTS,
    mimes: mimes.length ? mimes : DEFAULT_MIME,
  };
};

const hasAllowedExt = (name, exts) => !!name && exts.some((ext) => name.toLowerCase().endsWith(ext));

async function sniffMagic(file) {
  try {
    const buf = await file.slice(0, 12).arrayBuffer();
    const b = new Uint8Array(buf);

    // PDF
    if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'application/pdf';
    // PNG
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'image/png';
    // JPG
    if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'image/jpeg';
    // TIFF
    if (
      (b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2A && b[3] === 0x00)
      || (b[0] === 0x4D && b[1] === 0x4D && b[2] === 0x00 && b[3] === 0x2A)
    ) return 'image/tiff';
    // BMP
    if (b[0] === 0x42 && b[1] === 0x4D) return 'image/bmp';
    // HEIC/HEIF (ftyp)
    if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return 'image/heic';

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

const Input = ({
  name, text, accept, classes, onChange, onValidationError,
}) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const { exts, mimes } = parseAccept(accept);

  const emitError = (msg) => {
    setError(msg);
    if (onValidationError) onValidationError(msg);
  };

  const clearError = () => {
    setError('');
    if (onValidationError) onValidationError('');
  };

  const invalidate = (msg) => {
    emitError(msg);
    if (inputRef.current) inputRef.current.value = '';
  };

  const validateFile = async (file) => {
    if (file.size > MAX_BYTES) {
      return { ok: false, reason: 'size' };
    }

    const okExt = hasAllowedExt(file.name, exts);
    const okMime = file.type ? mimes.includes(file.type.toLowerCase()) : false;
    const sniff = await sniffMagic(file);
    // WAŻNE: nie przepuszczamy 'unknown'. Whitelist only.
    const okSniff = sniff !== 'unknown' && mimes.includes(sniff);

    const ok = okExt && (okMime || okSniff);
    return ok ? { ok: true } : { ok: false, reason: 'format' };
  };

  const handleChange = async (e) => {
    emitError(''); // czyścimy błąd na start

    const files = Array.from(e.target?.files || []);
    if (files.length === 0) return;

    // hard size check
    for (let i = 0; i < files.length; i += 1) {
      if (files[i].size > MAX_BYTES) {
        invalidate('Datei ist größer als 5 MB.');
        return;
      }
    }

    // walidacja równoległa
    const results = await Promise.all(files.map((f) => validateFile(f)));
    const bad = results.find((r) => !r.ok);

    if (bad) {
      const msg = bad.reason === 'size' ? 'Datei ist größer als 5 MB.' : 'Ungültiges Dateiformat.';
      invalidate(msg);
      return;
    }

    // przekaż TYLKO przefiltrowane pliki (na wszelki wypadek)
    const goodFiles = files.filter((_, i) => results[i].ok);
    if (goodFiles.length === 0) {
      invalidate('Ungültiges Dateiformat.');
      return;
    }

    clearError();
    // uwaga: zamiast e przekazujemy już listę dobrych plików
    if (onChange) onChange(goodFiles);
  };

  return (
    <label htmlFor="file" className={classes.label}>
      <span className={classes.icon}>
        <SvgAngehaengteDokumente />
      </span>
      <span className={classes.text}>{text}</span>
      <input
        ref={inputRef}
        type="file"
        name={name}
        title={text}
        accept={accept || DEFAULT_EXTS.join(',')}
        onChange={handleChange}
        className={classes.input}
      />
      {error && (
        <div
          style={{ color: '#c00' }}
        >
          {error}
        </div>
      )}
    </label>
  );
};

Input.propTypes = {
  name: PropTypes.string,
  text: PropTypes.string,
  onChange: PropTypes.func,
  accept: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string),
  onValidationError: PropTypes.func,
};

Input.defaultProps = {
  name: '',
  text: '',
  accept: '',
  classes: {},
  onChange: () => {},
  onValidationError: () => {},
};

export { Input as InputPlain };
export default injectSheet(styles)(Input);
